// SslVpnSettings.tsx
import { useState, useCallback, useEffect } from "react";
import { Loader2, Info, Shield, Lock, Users } from "lucide-react";
import { showError } from "../../../../../utils/toast";
import baseapi from "../../../../../services/baseapi";

// ── Types ─────────────────────────────────────────────────────────────────────
interface AuthRule {
    id: number;
    auth: string;
    portal: string;
    cipher: string;
    users: { name: string }[];
    groups: { name: string }[];
    "client-cert": string;
}

interface SslVpnData {
    port: number;
    status: string;
    algorithm: string;
    servercert: string;
    ciphersuite: string;
    "dns-server1": string;
    "dns-server2": string;
    "dtls-tunnel": string;
    "auth-timeout": number;
    "idle-timeout": number;
    "banned-cipher": string;
    "login-timeout": number;
    "default-portal": string;
    "tunnel-ip-pools": { name: string }[];
    "http-only-cookie": string;
    "login-block-time": number;
    "source-interface": { name: string }[];
    "ssl-max-proto-ver": string;
    "ssl-min-proto-ver": string;
    "dtls-max-proto-ver": string;
    "dtls-min-proto-ver": string;
    "login-attempt-limit": number;
    "force-two-factor-auth": string;
    "ssl-client-renegotiation": string;
    "ssl-insert-empty-fragment": string;
    "x-content-type-options": string;
    "authentication-rule": AuthRule[];
    [key: string]: any;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtProto = (val: string): string => {
    if (!val) return "—";
    return val
        .replace("tls1-", "TLS 1.")
        .replace("dtls1-", "DTLS 1.")
        .toUpperCase()
        .replace("TLS 1.", "TLS 1.");
};

const fmtSeconds = (s: number): string => {
    if (s >= 3600) return `${s} s (${s / 3600} hours)`;
    if (s >= 60) return `${s} s (${s / 60} min)`;
    return `${s} s`;
};

// ── Value Chip ────────────────────────────────────────────────────────────────
const Chip = ({ val, mono = false }: { val: string | undefined; mono?: boolean }) => {
    const str = String(val ?? "");
    if (!str) return <span className="text-gray-200 text-[11px]">—</span>;
    const lower = str.toLowerCase();
    const style =
        lower === "enable"
            ? "bg-green-500/15 border border-green-500/30 text-green-400"
            : lower === "disable"
                ? "bg-orange-500/15 border border-orange-500/30 text-orange-400"
                : lower === "high"
                    ? "bg-blue-500/15 border border-blue-500/30 text-blue-300"
                    : lower.startsWith("tls")
                        ? "bg-violet-500/15 border border-violet-500/30 text-violet-300"
                        : lower.startsWith("dtls")
                            ? "bg-cyan-500/15 border border-cyan-500/30 text-cyan-300"
                            : null;

    if (style) {
        return (
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase whitespace-nowrap ${style}`}>
                {str}
            </span>
        );
    }
    return (
        <span className={`text-[11px] text-gray-200 ${mono ? "font-mono" : ""}`}>{str}</span>
    );
};

// ── Row ───────────────────────────────────────────────────────────────────────
const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex items-center justify-between py-1.5 border-b border-gray-700 last:border-0 gap-4">
        <span className="text-[11px] text-gray-200 shrink-0 font-mono">{label}</span>
        <div className="text-right">{children}</div>
    </div>
);

// ── Section Card ──────────────────────────────────────────────────────────────
const SectionCard = ({ icon: Icon, title, iconColor, children }: {
    icon: any; title: string; iconColor: string; children: React.ReactNode;
}) => (
    <div className="bg-gray-800 border border-gray-800 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-800 flex items-center gap-2">
            <Icon size={14} className={iconColor} />
            <h3 className="text-[13px] font-bold text-white">{title}</h3>
        </div>
        <div className="px-5 py-1">{children}</div>
    </div>
);

// ── Main Component ────────────────────────────────────────────────────────────
export default function SslVpnSettings({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [data, setData] = useState<SslVpnData | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await baseapi.get("/firewall_sections", {
                params: {
                    query: JSON.stringify({
                        ...(asset_id && client_id ? { asset_id, client_id } : {}),
                        section_name: "ssl_vpn_settings",
                    }),
                    limit: 1,
                },
            });
            if (res.data.status && res.data.data?.[0]) {
                const raw = res.data.data[0].results;
                const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                setData(parsed);
            } else showError("Failed to load SSL VPN settings");
        } catch (e: any) { showError(e.message); }
        finally { setLoading(false); }
    }, [asset_id]);

    useEffect(() => { fetchData(); }, [fetchData]);

    if (loading) return (
        <div className="flex items-center justify-center gap-2 py-20">
            <Loader2 className="w-4 h-4 animate-spin text-green-400" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">Loading SSL VPN settings...</span>
        </div>
    );

    if (!data) return (
        <div className="flex flex-col items-center gap-2 py-16 opacity-40">
            <Info size={22} className="text-gray-200" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">No SSL VPN data available</span>
        </div>
    );

    const tlsMin = fmtProto(data["ssl-min-proto-ver"]);
    const tlsMax = fmtProto(data["ssl-max-proto-ver"]);
    const dtlsMin = fmtProto(data["dtls-min-proto-ver"]);
    const dtlsMax = fmtProto(data["dtls-max-proto-ver"]);
    const authRules = data["authentication-rule"] ?? [];

    return (
        <div className="space-y-4">

            {/* Page Header */}

            {/* 4 Summary Stat Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">

                {/* Status */}
                <div className="bg-gray-800 border border-gray-800 rounded-xl px-5 py-2">
                    <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest mb-2">Status</p>
                    {data.status ? (
                        <span className="text-[20px] font-black text-green-400 leading-none capitalize">{data.status === "enable" ? "Enabled" : "Disabled"}</span>
                    ) : "-"}
                    {data.port ? (
                        <p className="text-[10px] text-gray-200 mt-1.5">Port {data.port}</p>
                    ) : null}
                </div>

                {/* TLS Range */}
                <div className="bg-gray-800 border border-gray-800 rounded-xl px-5 py-2">
                    <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest mb-2">TLS Range</p>
                    {data["ssl-min-proto-ver"] && data["ssl-max-proto-ver"] ? (
                        <p className="text-[20px] font-black text-blue-400 leading-none font-mono">
                            {tlsMin.replace("TLS ", "")} – {tlsMax.replace("TLS ", "")}
                        </p>
                    ) : (
                        <p className="text-[20px] font-black text-blue-400 leading-none font-mono">-</p>
                    )}
                    {data["ssl-min-proto-ver"] ? <p className="text-[10px] text-gray-200 mt-1.5">Minimum {data["ssl-min-proto-ver"]}</p> : null}
                </div>

                {/* Certificate */}
                <div className="bg-gray-800 border border-gray-800 rounded-xl px-5 py-2">
                    <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest mb-2">Certificate</p>
                    {data.servercert ? (
                        <p className="text-[20px] font-black text-violet-400 leading-none font-mono">{data.servercert}</p>
                    ) : (
                        <p className="text-[20px] font-black text-violet-400 leading-none font-mono">-</p>
                    )}
                    {data.servercert ? <p className="text-[10px] text-gray-200 mt-1.5">ACME / Let's Encrypt</p> : null}
                </div>

                {/* Login Attempts */}
                <div className="bg-gray-800 border border-gray-800 rounded-xl px-5 py-4">
                    <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest mb-2">Login Attempts</p>
                    {data["login-attempt-limit"] ? (
                        <p className="text-[20px] font-black text-orange-400 leading-none">{data["login-attempt-limit"]}</p>
                    ) : (
                        <p className="text-[20px] font-black text-orange-400 leading-none">-</p>
                    )}
                    {data["login-block-time"] ? (
                        <p className="text-[10px] text-gray-200 mt-1.5">{data["login-block-time"]}-sec lockout after fail</p>
                    ) : null}
                </div>
            </div>

            {/* General + Cipher side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                {/* General Settings */}
                <SectionCard icon={Shield} title="General settings" iconColor="text-green-400">
                    <Row label="Status"><Chip val={data.status} /></Row>
                    <Row label="Port"><span className="text-[11px] text-gray-200 font-mono">{data.port}</span></Row>
                    <Row label="Certificate">
                        {data.servercert ? (
                            <span className="text-[11px] text-gray-200 font-mono">
                                {data.servercert} (ACME / Let's Encrypt)
                            </span>
                        ) : "-"}
                    </Row>
                    <Row label="Default portal">
                        <span className="text-[11px] text-gray-200 font-mono">{data["default-portal"]}</span>
                    </Row>

                    <Row label="Auth timeout">
                        {data["auth-timeout"] ? <span className="text-[11px] text-gray-200 font-mono">{fmtSeconds(data["auth-timeout"])}</span> : "-"}
                    </Row>
                    <Row label="Idle timeout">
                        {data["idle-timeout"] ? <span className="text-[11px] text-gray-200 font-mono">{fmtSeconds(data["idle-timeout"])}</span> : "-"}
                    </Row>
                    <Row label="Login attempts">
                        {data["login-attempt-limit"] ? <span className="text-[11px] text-gray-200 font-mono">{data["login-attempt-limit"]} tries</span> : "-"}
                    </Row>
                    <Row label="Login block time">
                        {data["login-block-time"] ? <span className="text-[11px] text-gray-200 font-mono">{data["login-block-time"]} s</span> : "-"}
                    </Row>
                    <Row label="Login timeout">
                        {data["login-timeout"] ? <span className="text-[11px] text-gray-200 font-mono">{data["login-timeout"]} s</span> : "-"}
                    </Row>
                    <Row label="Source interface">
                        {data["source-interface"] ? <span className="text-[11px] text-gray-200 font-mono">
                            {data["source-interface"]?.map(i => i.name).join(", ") || "—"}
                        </span> : "-"}
                    </Row>
                    <Row label="DNS server 1">
                        <span className="text-[11px] text-gray-200 font-mono">{data["dns-server1"]}</span>
                    </Row>
                    <Row label="DNS server 2">
                        <span className="text-[11px] text-gray-200 font-mono">{data["dns-server2"]}</span>
                    </Row>
                    <Row label="Tunnel IP pool">
                        <span className="text-[11px] text-gray-200 font-mono">
                            {data["tunnel-ip-pools"]?.[0]?.name || "—"}
                        </span>
                    </Row>
                    <Row label="DTLS tunnel"><Chip val={data["dtls-tunnel"]} /></Row>
                    <Row label="HTTP only cookie"><Chip val={data["http-only-cookie"]} /></Row>
                    <Row label="Force 2FA"><Chip val={data["force-two-factor-auth"]} /></Row>
                </SectionCard>

                {/* Cipher & TLS Settings */}
                <SectionCard icon={Lock} title="Cipher & TLS settings" iconColor="text-blue-400">
                    <Row label="SSL min proto">
                        <Chip val={tlsMin} />
                    </Row>
                    <Row label="SSL max proto">
                        <Chip val={tlsMax} />
                    </Row>
                    <Row label="Algorithm">
                        <span className="text-[11px] text-gray-200 font-mono font-bold">{data.algorithm}</span>
                    </Row>
                    <Row label="Ciphersuites (TLS1.3)">
                        <div className="flex flex-col gap-1 items-end">

                            {data.ciphersuite?.split(" ").map(c => (
                                <span key={c} className="text-[10px] text-gray-300 font-mono">{c}</span>
                            ))}
                        </div>
                    </Row>
                    <Row label="Banned ciphers">
                        <div className="flex gap-1 flex-wrap justify-end">
                            {data["banned-cipher"]?.split(" ").map(c => (
                                <span key={c} className="px-1.5 py-0.5 rounded text-[9px] font-bold font-mono bg-red-500/10 border border-red-500/20 text-red-400">
                                    {c}
                                </span>
                            ))}
                        </div>
                    </Row>
                    <Row label="Client renegotiation"><Chip val={data["ssl-client-renegotiation"]} /></Row>
                    <Row label="Insert empty fragment"><Chip val={data["ssl-insert-empty-fragment"]} /></Row>
                    <Row label="X-Content-Type-Options"><Chip val={data["x-content-type-options"]} /></Row>
                    <Row label="DTLS min proto">
                        <Chip val={dtlsMin} />
                    </Row>
                    <Row label="DTLS max proto">
                        <Chip val={dtlsMax} />
                    </Row>
                </SectionCard>
            </div>

            {/* Authentication Rules */}
            <div className="bg-gray-800 border border-gray-800 rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-800 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Users size={14} className="text-violet-400" />
                        <h3 className="text-[13px] font-bold text-white">Authentication rules</h3>
                    </div>
                    <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-violet-500/10 border border-violet-500/20 text-violet-400">
                        {authRules.length} rule{authRules.length !== 1 ? "s" : ""}
                    </span>
                </div>


                <div className="p-4 space-y-3">
                    {authRules.length === 0 ? (
                        <div className="flex flex-col items-center gap-2 py-16 opacity-40">
                            <Info size={22} className="text-gray-200" />
                            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">No authentication rules available</span>
                        </div>
                    ) : (
                        <div>
                            {authRules.map(rule => (
                                <div key={rule.id} className="border border-gray-700/60 rounded-xl overflow-hidden">
                                    {/* Rule header */}
                                    <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-800/40 flex-wrap">
                                        <span className="text-[11px] font-black text-blue-400">Rule {rule.id}</span>
                                        <span className="text-gray-200">·</span>
                                        <span className="text-[11px] text-gray-300 font-mono">Portal: <span className="text-white font-bold">{rule.portal}</span></span>
                                        <span className="text-gray-200">·</span>
                                        <span className="text-[11px] text-gray-300 font-mono">Cipher: <span className="text-white font-bold">{rule.cipher}</span></span>
                                    </div>

                                    {/* Rule body */}
                                    <div className="px-4 py-3 space-y-2.5 bg-gray-900/20">
                                        {/* Users */}
                                        {rule.users.length > 0 && (
                                            <div className="flex items-start gap-2">
                                                <span className="text-[10px] text-gray-200 font-bold uppercase tracking-widest mt-0.5 shrink-0 w-16">Users:</span>
                                                <div className="flex flex-wrap gap-1">
                                                    {rule.users.map(u => (
                                                        <span key={u.name} className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-500/10 border border-blue-500/20 text-blue-300">
                                                            {u.name}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Groups */}
                                        {rule.groups.length > 0 && (
                                            <div className="flex items-start gap-2">
                                                <span className="text-[10px] text-gray-200 font-bold uppercase tracking-widest mt-0.5 shrink-0 w-16">
                                                    Groups ({rule.groups.length}):
                                                </span>
                                                <div className="flex flex-wrap gap-1">
                                                    {rule.groups.map(g => (
                                                        <span key={g.name} className="px-2 py-0.5 rounded text-[10px] font-medium bg-purple-500/10 border border-purple-500/20 text-purple-300">
                                                            {g.name}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
